import React, { useState, useRef } from 'react';
import { Gamepad2, Flame, Sparkles, Star } from 'lucide-react';
import GameCard from '../game/GameCard';
import { Button } from '@/components/ui/button';
import { Game } from '@/types/game';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { GamePagination } from '@/components/ui/game-pagination';
import AdDisplay from '@/components/ads/AdDisplay';
import { useWebsiteSettings } from '@/hooks/useWebsiteSettings';
import { getAdsByPosition } from '@/utils/adService';

type FilterType = 'all' | 'trending' | 'new' | 'featured';

const GAMES_PER_PAGE = 24;

const ExploreGamesSection: React.FC = () => {
  const [activeFilter, setActiveFilter] = useState<FilterType>('all');
  const [currentPage, setCurrentPage] = useState(1);
  const { settings } = useWebsiteSettings();
  const sectionRef = useRef<HTMLElement>(null);
  
  // Get native ad frequency from settings (default to 8)
  const nativeAdFrequency = settings?.native_ad_frequency || 8;

  // Check if native ads are available
  const { data: nativeAds = [] } = useQuery({
    queryKey: ['native-ads'],
    queryFn: () => getAdsByPosition('between-games'),
    staleTime: 5 * 60 * 1000, // 5 minutes
  });

  // Fetch games data with pagination
  const { data: gamesData, isLoading } = useQuery({
    queryKey: ['explore-games', activeFilter, currentPage],
    queryFn: async () => {
      const offset = (currentPage - 1) * GAMES_PER_PAGE;
      
      let query = supabase
        .from('games')
        .select('*', { count: 'exact' })
        .eq('hidden', false);

      // Apply filters
      switch (activeFilter) {
        case 'trending':
          query = query.eq('trending', true);
          break;
        case 'new':
          query = query.eq('is_new', true);
          break;
        case 'featured':
          query = query.eq('featured', true);
          break;
      }

      query = query
        .order('created_at', { ascending: false })
        .range(offset, offset + GAMES_PER_PAGE - 1);

      const { data: games, error, count } = await query;

      if (error) throw error;

      const formattedGames: Game[] = (games || []).map((dbGame: any): Game => ({
        id: dbGame.id,
        title: dbGame.title,
        description: dbGame.description || '',
        thumbnail: dbGame.thumbnail || '',
        category: dbGame.category,
        tags: dbGame.tags || [],
        featured: dbGame.featured || false,
        trending: dbGame.trending || false,
        isNew: dbGame.is_new || false,
        playCount: dbGame.play_count || 0,
        fakePlayCount: dbGame.fake_play_count,
        rating: Number(dbGame.rating) || 0,
        createdAt: dbGame.created_at,
        updatedAt: dbGame.updated_at,
        embedType: 'iframe',
        gameUrl: dbGame.game_url || '',
      }));

      return {
        games: formattedGames,
        totalCount: count || 0,
      };
    },
    staleTime: 5 * 60 * 1000, // 5 minutes
  });

  const totalPages = Math.ceil((gamesData?.totalCount || 0) / GAMES_PER_PAGE);
  const games = gamesData?.games || [];

  // Insert native ads into games array only if ads exist
  const gamesWithAds = React.useMemo(() => {
    const result: (Game | { type: 'ad'; id: string })[] = [];
    
    games.forEach((game, index) => {
      result.push(game);
      
      // Add native ad every N games (based on settings) only if native ads exist
      if (nativeAds.length > 0 && (index + 1) % nativeAdFrequency === 0 && index < games.length - 1) {
        result.push({ type: 'ad', id: `ad-${index}` });
      }
    });
    
    return result;
  }, [games, nativeAdFrequency, nativeAds.length]);

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
    // Scroll to the section instead of top
    if (sectionRef.current) {
      sectionRef.current.scrollIntoView({ 
        behavior: 'smooth',
        block: 'start'
      });
    }
  };

  const handleFilterChange = (filter: FilterType) => {
    setActiveFilter(filter);
    setCurrentPage(1); // Reset to first page when filter changes
  };

  const filterButtons = [
    { key: 'all' as FilterType, label: 'All Games', icon: Gamepad2, color: 'bg-blue-500' },
    { key: 'trending' as FilterType, label: 'Trending', icon: Flame, color: 'bg-orange-500' },
    { key: 'new' as FilterType, label: 'New', icon: Sparkles, color: 'bg-green-500' },
    { key: 'featured' as FilterType, label: 'Featured', icon: Star, color: 'bg-purple-500' },
  ];

  if (isLoading) {
    return (
      <section className="mb-16">
        <div className="text-center mb-8">
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
            Explore <span className="bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">Games</span>
          </h2>
          <p className="text-gray-600 text-lg max-w-2xl mx-auto">
            Discover your next favorite game from our carefully curated collection
          </p>
        </div>
        
        <div className="flex flex-wrap justify-center gap-4 mb-8">
          {filterButtons.map((filter) => (
            <div key={filter.key} className="animate-pulse">
              <div className="h-12 w-32 bg-gray-200 rounded-full"></div>
            </div>
          ))}
        </div>
        
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-4">
          {Array.from({ length: 24 }).map((_, index) => (
            <div key={index} className="animate-pulse">
              <div className="bg-gray-200 w-full aspect-square rounded-lg mb-2"></div>
              <div className="bg-gray-200 h-4 rounded mb-1"></div>
              <div className="bg-gray-200 h-3 rounded w-3/4"></div>
            </div>
          ))}
        </div>
      </section>
    );
  }

  return (
    <section ref={sectionRef} className="mb-16">
      {/* Section Header */}
      <div className="text-center mb-8">
        <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
          Explore <span className="bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">Games</span>
        </h2>
        <p className="text-gray-600 text-lg max-w-2xl mx-auto">
          Discover your next favorite game from our carefully curated collection
        </p>
      </div>

      {/* Filter Buttons */}
      <div className="flex flex-wrap justify-center gap-4 mb-8">
        {filterButtons.map((filter) => {
          const Icon = filter.icon;
          const isActive = activeFilter === filter.key;
          
          return (
            <Button
              key={filter.key}
              onClick={() => handleFilterChange(filter.key)}
              variant={isActive ? "default" : "outline"}
              className={`
                relative px-6 py-3 rounded-full font-semibold transition-all duration-300 
                ${isActive 
                  ? `${filter.color} text-white shadow-lg hover:shadow-xl` 
                  : 'bg-white text-gray-700 border-2 border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                }
              `}
            >
              <Icon className="w-5 h-5 mr-2" />
              {filter.label}
            </Button>
          );
        })}
      </div>

      {/* Games Grid with Native Ads */}
      {games.length === 0 ? (
        <div className="text-center py-20">
          <div className="w-24 h-24 mx-auto mb-6 bg-gradient-to-br from-blue-100 to-purple-100 rounded-2xl flex items-center justify-center border border-blue-200 shadow-lg">
            <Gamepad2 className="w-10 h-10 text-blue-500" />
          </div>
          <h3 className="text-xl font-semibold text-gray-900 mb-2">No Games Found</h3>
          <p className="text-gray-600 max-w-md mx-auto">
            We couldn't find any games in this category. Try selecting a different filter!
          </p>
        </div>
      ) : (
        <>
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-4">
            {gamesWithAds.map((item, index) => {
              if ('type' in item && item.type === 'ad') {
                return (
                  <div key={item.id} className="animate-fade-in">
                    <AdDisplay position="between-games" className="w-full" />
                  </div>
                );
              }
              
              const game = item as Game;
              return (
                <div 
                  key={game.id} 
                  className="animate-fade-in"
                  style={{ animationDelay: `${index * 25}ms` }}
                >
                  <GameCard 
                    game={game} 
                    size="small"
                    priority={index < 6}
                  />
                </div>
              );
            })}
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <GamePagination
              currentPage={currentPage}
              totalPages={totalPages}
              onPageChange={handlePageChange}
            />
          )}
        </>
      )}
    </section>
  );
};

export default ExploreGamesSection;
